import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Star, Heart, Smile, Trophy, Dumbbell, Instagram, ArrowRight, ShieldCheck, VolumeX, BookOpen, Timer, ChevronDown, ShoppingCart } from 'lucide-react';
import { PRODUCTS } from '../data/products';
import { useCartStore } from '../store/useCartStore';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.5,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-104" data-mp="263kbtqu0htpe">
    {children}
  </motion.div>;
export function LandingPage() {
  const navigate = useNavigate();
  const {
    addItem,
    items,
    openCart
  } = useCartStore();
  const scrollToProducts = () => {
    const element = document.getElementById('products');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleBuyClick = (e: React.MouseEvent, product: (typeof PRODUCTS)[0]) => {
    e.stopPropagation();
    addItem(product);
  };
  return <div className="min-h-screen bg-brand-bg text-brand-text overflow-x-hidden selection:bg-brand-yellow selection:text-brand-blueDark pt-24" data-id="element-105" data-mp="3dbmy3v5cl31d">
      {/* БЛОК 1: Головний екран (Hero Section) */}
      <section className="relative pt-4 pb-16 lg:pt-8 lg:pb-20 overflow-hidden" data-id="element-106" data-mp="3n6olomjz5x1u">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative" data-id="element-107" data-mp="3rbechwzs0lfw">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-id="element-108" data-mp="4bnnc7eur8u">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} className="text-center lg:text-left order-1" data-id="element-109" data-mp="1ww3j2wwl18dx">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] mb-5 text-brand-blue uppercase tracking-tight" data-id="element-110" data-mp="308kcytbixxem">
                Це не просто <br data-id="element-111" data-mp="397xodrj7kw1z" />
                <span className="text-[1.1em] text-brand-blue block mt-1" data-id="element-112" data-mp="396mi9l2ajc7p">
                  Планер
                </span>
              </h1>

              <div className="hidden lg:inline-block border-l-2 border-brand-yellow pl-5 py-1 mb-8" data-id="element-113" data-mp="3o2tzcciejxir">
                <p className="text-lg md:text-xl font-medium text-brand-muted" data-id="element-114" data-mp="3j3r6dqfp5hr5">
                  Це спосіб навчити дитину <br className="hidden md:block" data-id="element-115" data-mp="toxqp08yfcg" />
                  <span className="text-brand-yellow" data-id="element-116" data-mp="3swpwjcqudgcv">самостійності</span> без
                  криків
                </p>
              </div>

              <div className="hidden lg:flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-117" data-mp="anpc7hhf4wxu">
                <button onClick={scrollToProducts} className="px-8 py-4 bg-brand-blue hover:bg-brand-blueDark text-white rounded-xl font-bold text-base tracking-wide shadow-button transition-colors duration-200 flex items-center justify-center gap-2" data-id="element-118" data-mp="5p4dxnlq8nc1">
                  Замовити планер
                  <ArrowRight className="w-4 h-4" strokeWidth={2} data-id="element-119" data-mp="kcuty9frkf7j" />
                </button>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="relative order-2 max-w-md mx-auto lg:max-w-full flex flex-col items-center" data-id="element-120" data-mp="6ky8upcij5kn">
              <div className="relative rounded-lg overflow-hidden shadow-card border border-black/5 bg-white mb-6 w-full" data-id="element-121" data-mp="3nu4paq3bsixa">
                <img src="https://cdn.magicpatterns.com/uploads/cdrFBjpV29uCPC4QsJUG8h/IMG_5206.png" alt="MyPlaner - Інтерактивний планер для дітей" className="w-full h-auto object-cover" data-id="element-122" data-mp="3gq4w8pa36t1i" />
              </div>

              <div className="lg:hidden inline-block mb-6 text-center w-full" data-id="element-123" data-mp="322tlgplqbd2g">
                <p className="text-lg font-medium text-brand-muted" data-id="element-124" data-mp="2y0otrweucqig">
                  Це спосіб навчити дитину <br data-id="element-125" data-mp="2xyc6r4u87pyj" />
                  <span className="text-brand-yellow" data-id="element-126" data-mp="2t12g5lv3p894">самостійності</span> без
                  криків
                </p>
              </div>

              <div className="flex lg:hidden flex-col sm:flex-row gap-4 w-full justify-center" data-id="element-127" data-mp="2o30woztrj6x4">
                <button onClick={scrollToProducts} className="w-full px-8 py-4 bg-brand-blue hover:bg-brand-blueDark text-white rounded-xl font-bold text-base tracking-wide shadow-button transition-colors duration-200 flex items-center justify-center gap-2" data-id="element-128" data-mp="2j4z6luxx67c5">
                  Замовити планер
                  <ArrowRight className="w-4 h-4" strokeWidth={2} data-id="element-129" data-mp="tuf08u2p1vbv" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* БЛОК 2: Болі батьків (Проблема) */}
      <section className="py-16 bg-white relative border-y border-black/5" data-id="element-130" data-mp="13kq82rpi0oo1">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-131" data-mp="6uh60zp3h0v0">
          <FadeIn data-id="element-132" data-mp="6w0lwp6u99w9">
            <div className="text-center mb-12" data-id="element-133" data-mp="6wk66cjyoadg">
              <h2 className="text-3xl md:text-4xl font-black text-brand-blue mb-4" data-id="element-134" data-mp="awu9fhgdcmom">
                Знайомі ситуації?
              </h2>
              <p className="text-lg text-brand-muted max-w-2xl mx-auto font-medium" data-id="element-135" data-mp="fwyuzlw4w7lp">
                Кожен день схожий на день бабака, а прості справи перетворюються
                на випробування.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-136" data-mp="1w8cgqedl2jhr">
            {[{
            icon: <VolumeX className="w-6 h-6 text-brand-blue" data-id="element-137" data-mp="16k735mf09d6m" />,
            title: 'Нескінченні нагадування',
            desc: 'Втомилися щодня повторювати «почисть зуби», «прибери іграшки» або «заправ ліжко»?'
          }, {
            icon: <BookOpen className="w-6 h-6 text-brand-blue" data-id="element-138" data-mp="1ggtn88zic07l" />,
            title: 'Сварки через уроки',
            desc: 'Дитина сідає за домашнє завдання тільки після довгих вмовлянь та зіпсованого настрою?'
          }, {
            icon: <Timer className="w-6 h-6 text-brand-blue" data-id="element-139" data-mp="iwqohmknxgy5" />,
            title: 'Стресові ранки',
            desc: 'Ранкові збори в садок або школу перетворюються на хаос і забирають енергію на весь день?'
          }].map((item, idx) => <FadeIn key={idx} delay={idx * 0.1} data-id="element-140" data-mp="53hd5gd5mlcz" data-mpi={idx}>
                <div className="bg-brand-bg rounded-2xl p-6 border border-gray-100 hover:border-brand-blue/20 transition-all h-full flex flex-col" data-id="element-141" data-mp="5269dgjpldlv">
                  <div className="bg-white w-12 h-12 rounded-xl shadow-sm flex items-center justify-center mb-5" data-id="element-142" data-mp="2brrmlbxd4baf">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-brand-blue" data-id="element-143" data-mp="21u3nk9sq4hmg">
                    {item.title}
                  </h3>
                  <p className="text-brand-muted text-base font-medium leading-relaxed" data-id="element-144" data-mp="26sexslmxku57">
                    {item.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* БЛОК 3: Об'єднаний блок (Фото + Як працює + Купити) */}
      <section className="py-12 lg:py-20 bg-brand-bg" data-id="element-145" data-mp="2lh1byk0tnfuu">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-146" data-mp="2lkfhg14s856i">
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 overflow-hidden flex flex-col lg:grid lg:grid-cols-2" data-id="element-147" data-mp="2pi3dj7i7o3zz">
            {/* Ліва частина: Фото планера (Тільки для ПК) */}
            <div className="hidden lg:block relative bg-brand-blue/5 overflow-hidden min-h-[500px]" data-id="element-148" data-mp="1e5u77nea4m2p">
              <img src="https://cdn.magicpatterns.com/uploads/w3mah8GUReZcNXMHpqYgWg/IMG_5249.jpg" alt="Дівчинка заповнює планер" className="absolute inset-0 w-full h-full object-cover object-center" data-id="element-149" data-mp="25tdqr3so2gzu" />
            </div>

            {/* Права частина: Опис, Мобільне фото, Ціна та кнопка */}
            <div className="p-6 lg:p-12 flex flex-col" data-id="element-150" data-mp="2oj3ljqwfj2rr">
              <FadeIn data-id="element-151" data-mp="2okob123vzvir">
                <h2 className="text-3xl md:text-4xl font-black text-brand-blue mb-6" data-id="element-152" data-mp="2kh8iaguxufbc">
                  Як працює MyPlaner?
                </h2>

                <div className="space-y-5 mb-8 lg:mb-10" data-id="element-153" data-mp="2foytx59h1nb6">
                  {[{
                  title: 'Формує корисні звички',
                  desc: 'Вмивання, прибирання, читання стають автоматичними діями.'
                }, {
                  title: 'Мотивує через гру та нагороди',
                  desc: 'Збери 7 зірочок за тиждень — отримай заслужений приз!'
                }, {
                  title: 'Зменшує стрес і нагадування',
                  desc: 'Більше жодних нагадувань. Дитина сама бачить свій план.'
                }, {
                  title: 'Зона Workout',
                  desc: 'Фізична активність щодня: присідання, віджимання, планка.'
                }].map((item, idx) => <div key={idx} className="flex gap-4" data-id="element-154" data-mp="2is9dungph6d" data-mpi={idx}>
                      <div className="flex-shrink-0 mt-1" data-id="element-155" data-mp="2h7eu515dy8i">
                        <CheckCircle2 className="w-6 h-6 text-brand-yellow fill-brand-yellow/20" data-id="element-156" data-mp="7blb09nmw9e8" />
                      </div>
                      <div data-id="element-157" data-mp="cbg32gj5b50v">
                        <h4 className="text-lg font-bold text-brand-blue" data-id="element-158" data-mp="gb8f537qztaz">
                          {item.title}
                        </h4>
                        <p className="text-brand-muted font-medium text-sm" data-id="element-159" data-mp="latg1j6habqc">
                          {item.desc}
                        </p>
                      </div>
                    </div>)}
                </div>

                {/* Фото планера (Тільки для мобільних, між текстом і ціною) */}
                <div className="lg:hidden mb-8 flex items-center justify-center" data-id="element-160" data-mp="1q4381imzef3e">
                  <div className="relative rounded-lg overflow-hidden shadow-card border border-black/5" data-id="element-161" data-mp="1uww8vvfbx3a7">
                    <img src="https://cdn.magicpatterns.com/uploads/w3mah8GUReZcNXMHpqYgWg/IMG_5249.jpg" alt="Дівчинка заповнює планер" className="w-full h-auto object-cover" data-id="element-162" data-mp="1yx6eh6z7lroj" />
                  </div>
                </div>

                <div className="bg-brand-bg rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-6 border border-gray-100" data-id="element-163" data-mp="2du77rex1tup4">
                  <div className="text-center sm:text-left" data-id="element-164" data-mp="jgft20zv4kmt">
                    <p className="text-sm text-brand-muted font-bold uppercase tracking-wider mb-1" data-id="element-165" data-mp="jj207ziucy4i">
                      Вартість планера
                    </p>
                    <div className="text-4xl font-black text-brand-blue" data-id="element-166" data-mp="2rzpeo1uakhjt">
                      1000 <span className="text-2xl" data-id="element-167" data-mp="2ny46uuwm6tgh">грн</span>
                    </div>
                  </div>
                  <button onClick={scrollToProducts} className="w-full sm:w-auto px-8 py-4 bg-brand-blue hover:bg-brand-blueDark text-white rounded-xl font-bold text-base tracking-wide shadow-button transition-colors duration-200 flex items-center justify-center gap-2" data-id="element-168" data-mp="2j0vlymk30bbj">
                    Купити зараз
                    <ArrowRight className="w-4 h-4" strokeWidth={2} data-id="element-169" data-mp="31qal2bg7iqda" />
                  </button>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* БЛОК ТОВАРІВ (Магазин) */}
      <section id="products" className="py-20 bg-white" data-id="element-170" data-mp="1qa3rpumktwqu">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-171" data-mp="1rsauoeog4a8n">
          <FadeIn data-id="element-172" data-mp="1rsu2h2kdti8y">
            <div className="text-center mb-16" data-id="element-173" data-mp="1rq7uj1tkx0q7">
              <h2 className="text-4xl md:text-5xl font-black text-brand-blue mb-4" data-id="element-174" data-mp="1wrngcw3kzb9n">
                Оберіть свій дизайн
              </h2>
              <p className="text-lg text-brand-muted font-medium max-w-2xl mx-auto" data-id="element-175" data-mp="1ircp3ryczuny">
                У нас є ідеальний планер як для маленьких принцес, так і для
                майбутніх чемпіонів.
              </p>
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-176" data-mp="1dt2upjkgky3e">
            {PRODUCTS.map((product, idx) => <FadeIn key={product.id} delay={idx * 0.1} data-id="element-177" data-mp="10261bqipkcva" data-mpi={idx}>
                <div onClick={() => navigate(`/product/${product.id}`)} className="bg-brand-bg rounded-3xl overflow-hidden border border-gray-100 hover:shadow-card transition-all group flex flex-col h-full cursor-pointer transform hover:-translate-y-1" data-id="element-178" data-mp="142q0t0qr7skl">
                  <div className="relative aspect-[3/4] overflow-hidden bg-white" data-id="element-179" data-mp="bfzz9g4i41x5">
                    <img src={product.img} alt={product.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" data-id="element-180" data-mp="6h6ldmlay8wg" />
                  </div>
                  <div className="p-6 flex flex-col flex-grow" data-id="element-181" data-mp="ge1hjyu3rx7k">
                    <h3 className="text-xl font-black text-brand-blue mb-2" data-id="element-182" data-mp="uece9ii3g60n">
                      {product.title}
                    </h3>
                    <p className="text-brand-muted text-sm font-medium mb-6 flex-grow" data-id="element-183" data-mp="pdyd8kewtwc4">
                      {product.desc}
                    </p>

                    <div className="flex items-center justify-between mt-auto" data-id="element-184" data-mp="z5kk64k7t992">
                      <div className="text-2xl font-black text-brand-blue" data-id="element-185" data-mp="1d5c5kqd29rx3">
                        {product.price} <span className="text-sm" data-id="element-186" data-mp="1940fkjq9z4od">грн</span>
                      </div>
                      <button onClick={e => handleBuyClick(e, product)} className="border border-brand-blue/25 text-brand-blue hover:bg-brand-blue hover:text-white px-5 py-2.5 rounded-xl font-semibold text-sm tracking-wide transition-colors duration-200 flex items-center gap-2" data-id="element-187" data-mp="19275eugge2bm">
                        Купити
                      </button>
                    </div>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* БЛОК FAQ: Питання та відповіді */}
      <section className="py-16 bg-brand-bg" data-id="element-188" data-mp="2tm2jids6ddbp">
        <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-189" data-mp="2tl9jrwrmvo32">
          <FadeIn data-id="element-190" data-mp="2xnebgpyiuan2">
            <div className="text-center mb-12" data-id="element-191" data-mp="2xnwfi8rfdt5j">
              <h2 className="text-3xl md:text-4xl font-black text-brand-blue mb-4" data-id="element-192" data-mp="2xljn11o1k0e8">
                Часті запитання
              </h2>
            </div>
          </FadeIn>

          <div className="space-y-4" data-id="element-193" data-mp="cw40wjzrozux">
            {[{
            q: 'Для якого віку підходить планер?',
            a: 'Планер ідеально підходить для дітей від 4 до 10 років. Малюки можуть орієнтуватися по картинках, а старші діти — читати завдання самостійно.'
          }, {
            q: 'Як він кріпиться?',
            a: 'Планер має повністю магнітну основу. Він надійно тримається на холодильнику або будь-якій іншій металевій поверхні і не сповзає.'
          }, {
            q: 'Чим писати на планері?',
            a: 'У комплекті йде спеціальний маркер з губкою. Дитина може легко ставити галочки та стирати їх наприкінці тижня. Поверхня планера багаторазова.'
          }].map((faq, idx) => {
            const [isOpen, setIsOpen] = useState(false);
            return <FadeIn key={idx} delay={idx * 0.1} data-id="element-194" data-mp="3019ql37c2jhz" data-mpi={idx}>
                  <div className="border border-black/5 rounded-2xl overflow-hidden bg-white" data-id="element-195" data-mp="2v387wjdtkm71">
                    <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-196" data-mp="2v1dhwqnoy3w3">
                      <span className="font-bold text-brand-blue text-lg" data-id="element-197" data-mp="357oml9fkbx99">
                        {faq.q}
                      </span>
                      <ChevronDown className={`w-5 h-5 text-brand-blue transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-198" data-mp="3a714rgvhog5i" />
                    </button>
                    <AnimatePresence data-id="element-199" data-mp="5r8fxod9pcdg">
                      {isOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-200" data-mp="3nwl8aoww9hfv">
                          <div className="px-6 pb-5 text-brand-muted font-medium" data-id="element-201" data-mp="2iy7s0spo01ft">
                            {faq.a}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </FadeIn>;
          })}
          </div>
        </div>
      </section>

      {/* БЛОК 5: Соціальний доказ та довіра */}
      <section className="py-16 bg-brand-bg" data-id="element-202" data-mp="2016nf900w3ce">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-203" data-mp="1v8n6c224mq2u">
          <FadeIn data-id="element-204" data-mp="1v72e4j3abj85">
            <div className="flex justify-center mb-8" data-id="element-205" data-mp="17jkrf1yvnbc1">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-brand-blue/15" data-id="element-206" data-mp="17hzw3a4qnod4">
                <ShieldCheck className="w-4 h-4 text-brand-yellow" data-id="element-207" data-mp="12k7wrv1rkzyw" />
                <span className="font-medium text-brand-muted text-xs uppercase tracking-[0.14em]" data-id="element-208" data-mp="hzpjyiabsk7z">
                  Зроблено в Україні
                </span>
              </div>
            </div>

            <div className="text-center mb-12" data-id="element-209" data-mp="qyaze4ki3j4j">
              <h2 className="text-3xl md:text-4xl font-black text-brand-blue mb-4" data-id="element-210" data-mp="r1p3picq62em">
                Батьки рекомендують
              </h2>
              <p className="text-lg text-brand-muted font-medium" data-id="element-211" data-mp="10ybne3tbenf2">
                Сотні сімей вже забули про крики вранці.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-212" data-mp="19q3x2cailsil">
            {[{
            name: 'Анна, мама 6-річного Максима',
            text: 'Це просто порятунок! Раніше кожен ранок починався зі сліз та вмовлянь почистити зуби. Тепер син сам біжить до холодильника відмічати галочки. Чекає кінця тижня, щоб отримати свій приз.',
            img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
          }, {
            name: 'Олена, мама двійнят',
            text: "Купили відразу два планери. У дітей з'явився змагальний дух! Самі заправляють ліжко, роблять зарядку. Я нарешті можу спокійно випити каву вранці.",
            img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
          }, {
            name: 'Ігор, тато 8-річної Софії',
            text: "Відмінна річ для привчання до дисципліни без тиску. Донька сама обирає собі 'Завдання тижня' і дуже пишається, коли збирає всі зірочки. Якість магніту супер!",
            img: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
          }].map((review, idx) => <FadeIn key={idx} delay={idx * 0.1} data-id="element-213" data-mp="9pryoxoz65sf" data-mpi={idx}>
                <div className="bg-white p-6 rounded-2xl h-full flex flex-col border border-black/5" data-id="element-214" data-mp="9nxexmpfwjpx">
                  <div className="flex gap-1 mb-4" data-id="element-215" data-mp="208rmkscre65q">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-brand-yellow fill-brand-yellow" data-id="element-216" data-mp="1ve44bb8cm7zl" data-mpi={i} />)}
                  </div>
                  <p className="text-brand-text font-medium text-base leading-relaxed mb-6 flex-grow" data-id="element-217" data-mp="2j1c7nnm7v6yz">
                    "{review.text}"
                  </p>
                  <div className="flex items-center gap-3" data-id="element-218" data-mp="2f0a5axx06ess">
                    <img src={review.img} alt={review.name} className="w-10 h-10 rounded-full object-cover" data-id="element-219" data-mp="2a88nyxkz9xvw" />
                    <p className="font-bold text-brand-blue text-sm" data-id="element-220" data-mp="32puuhyjr28ai">
                      {review.name}
                    </p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* БЛОК 6: Підвал (Footer) */}
      <footer className="bg-brand-blueDark text-white pt-16 pb-10 relative overflow-hidden" data-id="element-221" data-mp="3a8c5k979xv5l">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10" data-id="element-222" data-mp="3f86ui77hoao0">
          <FadeIn data-id="element-223" data-mp="3j9k3gq5votz5">
            <h2 className="text-3xl md:text-4xl font-black mb-4" data-id="element-224" data-mp="3ja1vtbpf6o1s">
              Готові до спокійних ранків?
            </h2>
            <p className="text-lg text-white/60 mb-8 max-w-xl mx-auto font-normal" data-id="element-225" data-mp="3o7lohgmv73d9">
              Замовте MyPlaner сьогодні та почніть формувати корисні звички
              вашої дитини вже завтра.
            </p>

            <button className="px-8 py-4 bg-brand-yellow hover:bg-brand-yellowHover text-brand-blueDark rounded-xl font-bold text-base tracking-wide shadow-button transition-colors duration-200 mb-12 inline-flex items-center gap-2" data-id="element-226" data-mp="1tkbeuzesslk">
              Купити за 1000 грн
              <ArrowRight className="w-4 h-4" strokeWidth={2} data-id="element-227" data-mp="fs1x6n5r7v0u" />
            </button>
          </FadeIn>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-228" data-mp="2bv6p03ws0dbe">
            <div className="text-2xl font-black tracking-tight flex items-center" data-id="element-229" data-mp="27vnzeuu0fe28">
              <span className="text-brand-yellow font-script italic mr-1" data-id="element-230" data-mp="2lmt5ofrvv4lj">
                My
              </span>
              <span className="text-white" data-id="element-231" data-mp="2gu9jwvj1l35n">Planer</span>
            </div>

            <div className="flex items-center gap-4" data-id="element-232" data-mp="2vj5zrercbkgt">
              <a href="https://www.instagram.com/myplaner.ua/p/DXq4XE3jJA8/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors duration-200" data-id="element-233" data-mp="2vm1teqw5nmzg">
                <Instagram className="w-5 h-5" data-id="element-234" data-mp="2zjg6itmyiewq" />
                <span className="font-bold" data-id="element-235" data-mp="1a5t6d074bpi0">@myplaner.ua</span>
              </a>
            </div>
          </div>

          <div className="mt-8 text-white/40 text-sm flex flex-col md:flex-row justify-center gap-4 font-normal" data-id="element-236" data-mp="158t8hckf9t1z">
            <a href="#" className="hover:text-white transition-colors" data-id="element-237" data-mp="3gvejkvkrun6k">
              Умови доставки та оплати
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-238" data-mp="3lvsjfqc7yqti">
              Політика конфіденційності
            </a>
            <span data-id="element-239" data-mp="3c3mruhpvqbeu">
              © {new Date().getFullYear()} MyPlaner. Всі права захищені.
            </span>
          </div>
        </div>
      </footer>
    </div>;
}